import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { User, Mail, Briefcase, FileText, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { useSettingsStore } from '@/store/settings.store'

import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useAuthStore } from '@/store/auth.store'
import { employeesService } from '@/services/employeesService'
import { shiftService } from '@/services/shiftService'
import {
  GenderLookup,
  DepartmentLookup,
  CountryLookup,
  ProfessionIqamaLookup,
  NoticePeriodLookup,
  MaritalStatusLookup,
} from '@/api/types'
import type { ShiftListItem } from '@/api/types'

// Validation schema – aligned with /api/Employee/SaveEmployee and UpdateEmployee (multipart/form-data)
const employeeSchema = z.object({
  // Personal Information - English (API fields only)
  EmpFirstNameEn: z.string().min(1, 'First name (English) is required'),
  EmpFatherNameEn: z.string().optional(),
  EmpGrandNameEn: z.string().optional(),
  EmpFamilyNameEn: z.string().optional(),

  // Personal Information - Arabic
  EmpFirstNameAr: z.string().optional(),
  EmpFatherNameAr: z.string().optional(),
  EmpGrandNameAr: z.string().optional(),
  EmpFamilyNameAr: z.string().optional(),

  // Contact
  EmpEmail: z.string().email('Invalid email address'),
  EmpMobileNo: z.string().refine(
    (val) => {
      if (!val) return true
      const cleaned = (val || '').replace(/\s+/g, '')
      if (cleaned.startsWith('+966')) return cleaned.length === 13
      if (cleaned.startsWith('5') || cleaned.startsWith('9')) return cleaned.length === 9
      return false
    },
    { message: 'Mobile must be +966XXXXXXXXX or start with 5/9 and be 9 digits' }
  ).optional(),

  EmpDepartmetId: z.number().optional().or(z.literal(0)),
  EmpGenderId: z.number().min(1, 'Gender is required'),
  EmpDob: z.string().min(1, 'Date of birth is required'),
  EmpBirthPlace: z.string().optional(),
  EmpNationalityId: z.number().optional(),
  EmpAge: z.string().optional(),

  // Employment (API)
  EmpCode: z.string().optional(),
  EmpPostedLocation: z.string().optional(),
  EmpReferenceId: z.string().optional(),
  EmpReferenceContact: z.string().optional(),
  NoticePeriod: z.number().optional(),
  Status: z.number().optional(),
  IsActive: z.union([z.literal(0), z.literal(1)]).optional(),

  // Documents
  IqamaNo: z.string().optional(),
  IqamaExpiryDate: z.string().optional(),
  PassportNo: z.string().optional(),
  PassportExpiryDate: z.string().optional(),
  OtherNationalityId: z.number().optional(),
  MaritalStatus: z.number().optional(),
  ProfessionIqama: z.number().optional(),
  ContactPersonNumber: z.string().optional(),
  ShiftId: z.number().optional().or(z.literal(0)),
})

type EmployeeFormData = z.infer<typeof employeeSchema>

/**
 * Employee Edit/Create Page
 * Form for creating new employees or editing existing ones
 */
export const EmployeesEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  
  const isEditing = Boolean(id)
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)

  // Lookup data
  const [genders, setGenders] = useState<GenderLookup[]>([])
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [countries, setCountries] = useState<CountryLookup[]>([])
  const [professions, setProfessions] = useState<ProfessionIqamaLookup[]>([])
  const [noticePeriods, setNoticePeriods] = useState<NoticePeriodLookup[]>([])
  const [maritalStatuses, setMaritalStatuses] = useState<MaritalStatusLookup[]>([])
  const [shifts, setShifts] = useState<ShiftListItem[]>([])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<EmployeeFormData>({
    resolver: zodResolver(employeeSchema),
    defaultValues: {
      Status: 1,
      IsActive: 1,
    },
  })

  // Load lookup data
  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [gendersData, deptsData, countriesData, professionsData, noticeData, maritalData] =
          await Promise.all([
            employeesService.getGenderLookups(),
            employeesService.getDepartmentLookups(),
            employeesService.getCountryLookups(),
            employeesService.getProfessionIqamaLookups(),
            employeesService.getNoticePeriodLookups(),
            employeesService.getMaritalStatusLookups(),
          ])

        setGenders(gendersData || [])
        setDepartments(deptsData || [])
        setCountries(countriesData || [])
        setProfessions(professionsData || [])
        setNoticePeriods(noticeData || [])
        setMaritalStatuses(maritalData || [])
      } catch (error) {
        console.error('Error loading lookups:', error)
        toast.error('Failed to load form data')
      }
    }

    loadLookups()
  }, [])

  // Load shifts for Shift dropdown (branch-specific)
  useEffect(() => {
    if (!branchId) return
    const loadShifts = async () => {
      try {
        const res = await shiftService.getAll(branchId, 1, 500)
        setShifts(res.data ?? [])
      } catch (e) {
        console.error('Error loading shifts:', e)
      }
    }
    loadShifts()
  }, [branchId])

  // Load employee data for editing
  useEffect(() => {
    if (isEditing && branchId && id) {
      const loadEmployee = async () => {
        try {
          setLoading(true)
          const employee = await employeesService.getEmployeeById(branchId, Number(id))
          
          console.log('Loaded employee data:', employee)
          
          // Map camelCase API response to form fields (PascalCase – matches SaveEmployee/UpdateEmployee API)
          const fieldMap: Record<string, keyof EmployeeFormData> = {
            empCode: 'EmpCode',
            empDepartmetId: 'EmpDepartmetId',
            empPostedLocation: 'EmpPostedLocation',
            empReferenceId: 'EmpReferenceId',
            empFirstNameEn: 'EmpFirstNameEn',
            empFatherNameEn: 'EmpFatherNameEn',
            empGrandNameEn: 'EmpGrandNameEn',
            empFamilyNameEn: 'EmpFamilyNameEn',
            empFirstNameAr: 'EmpFirstNameAr',
            empFatherNameAr: 'EmpFatherNameAr',
            empGrandNameAr: 'EmpGrandNameAr',
            empFamilyNameAr: 'EmpFamilyNameAr',
            empEmail: 'EmpEmail',
            empMobileNo: 'EmpMobileNo',
            empDob: 'EmpDob',
            empBirthPlace: 'EmpBirthPlace',
            empNationalityId: 'EmpNationalityId',
            empAge: 'EmpAge',
            empGenderId: 'EmpGenderId',
            empReferenceContact: 'EmpReferenceContact',
            iqamaNo: 'IqamaNo',
            iqamaExpiryDate: 'IqamaExpiryDate',
            passportNo: 'PassportNo',
            passportExpiryDate: 'PassportExpiryDate',
            otherNationalityId: 'OtherNationalityId',
            maritalStatus: 'MaritalStatus',
            professionIqama: 'ProfessionIqama',
            contactPersonNumber: 'ContactPersonNumber',
            noticePeriod: 'NoticePeriod',
            status: 'Status',
            shiftId: 'ShiftId',
            isActive: 'IsActive',
          }

          // Set form values using mapping
          Object.entries(employee).forEach(([key, value]) => {
            const formField = fieldMap[key]
            if (formField && value !== undefined && value !== null && value !== '') {
              setValue(formField, value as any)
            }
          })

          // Set image preview if exists
          if (employee.imageUrl || employee.profileImageUrl) {
            setImagePreview(employee.imageUrl || employee.profileImageUrl || null)
          }
        } catch (error: any) {
          console.error('Error loading employee:', error)
          toast.error(error.response?.data?.message || 'Failed to load employee data')
          navigate('/employees')
        } finally {
          setLoading(false)
        }
      }

      loadEmployee()
    }
  }, [isEditing, branchId, id, setValue, navigate])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const onSubmit = async (data: EmployeeFormData) => {
    if (!branchId) {
      await showError('Branch required', 'Please select a branch first.')
      return
    }

    try {
      setLoading(true)

      // Build FormData exactly like Swagger: all keys present, same types (int as "0", date as ISO)
      const now = new Date().toISOString()
      const companyId = (user?.selectedBranch as any)?.companyId ?? 1
      const dateTimeFields = ['EmpDob', 'IqamaExpiryDate', 'PassportExpiryDate']
      const toStr = (key: string, value: unknown): string => {
        if (value === undefined || value === null || value === '') return ''
        if (dateTimeFields.includes(key) && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return `${value}T00:00:00.000Z`
        }
        return String(value)
      }
      const formData = new FormData()
      const raw: Record<string, string> = {
        Id: isEditing && id ? id : '0',
        EmpCode: toStr('EmpCode', data.EmpCode),
        EmpDepartmetId: String(data.EmpDepartmetId ?? 0),
        EmpPostedLocation: toStr('EmpPostedLocation', data.EmpPostedLocation),
        EmpReferenceId: toStr('EmpReferenceId', data.EmpReferenceId),
        EmpFirstNameEn: toStr('EmpFirstNameEn', data.EmpFirstNameEn),
        EmpGrandNameEn: toStr('EmpGrandNameEn', data.EmpGrandNameEn),
        EmpFatherNameEn: toStr('EmpFatherNameEn', data.EmpFatherNameEn),
        EmpFirstNameAr: toStr('EmpFirstNameAr', data.EmpFirstNameAr),
        EmpGrandNameAr: toStr('EmpGrandNameAr', data.EmpGrandNameAr),
        EmpFatherNameAr: toStr('EmpFatherNameAr', data.EmpFatherNameAr),
        EmpFamilyNameEn: toStr('EmpFamilyNameEn', data.EmpFamilyNameEn),
        EmpFamilyNameAr: toStr('EmpFamilyNameAr', data.EmpFamilyNameAr),
        EmpEmail: toStr('EmpEmail', data.EmpEmail),
        EmpMobileNo: toStr('EmpMobileNo', data.EmpMobileNo),
        EmpDob: toStr('EmpDob', data.EmpDob),
        EmpBirthPlace: toStr('EmpBirthPlace', data.EmpBirthPlace),
        EmpNationalityId: String(data.EmpNationalityId ?? 0),
        EmpAge: toStr('EmpAge', data.EmpAge),
        EmpGenderId: String(data.EmpGenderId ?? 0),
        EmpReferenceContact: toStr('EmpReferenceContact', data.EmpReferenceContact),
        IqamaNo: toStr('IqamaNo', data.IqamaNo),
        IqamaExpiryDate: toStr('IqamaExpiryDate', data.IqamaExpiryDate),
        PassportNo: toStr('PassportNo', data.PassportNo),
        PassportExpiryDate: toStr('PassportExpiryDate', data.PassportExpiryDate),
        OtherNationalityId: String(data.OtherNationalityId ?? 0),
        MaritalStatus: String(data.MaritalStatus ?? 0),
        ProfessionIqama: String(data.ProfessionIqama ?? 0),
        ContactPersonNumber: toStr('ContactPersonNumber', data.ContactPersonNumber),
        NoticePeriod: String(data.NoticePeriod ?? 0),
        ShiftId: String(data.ShiftId ?? 0),
        Status: String(data.Status ?? 1),
        IsActive: String(data.IsActive ?? 1),
        CreatedById: String(user?.userId ?? 0),
        CreatedDate: now,
        UpdatedById: String(user?.userId ?? 0),
        UpdatedDate: now,
        CompanyId: String(companyId),
        BranchId: String(branchId),
      }
      Object.entries(raw).forEach(([k, v]) => formData.append(k, v))
      if (selectedImage) {
        formData.append('profileImage', selectedImage)
      }

      const payload: Record<string, string> = {}
      formData.forEach((v, k) => {
        payload[k] = v instanceof File ? `[File: ${v.name}]` : String(v)
      })
      console.log('Employee save payload:', payload)

      let response
      if (isEditing) {
        response = await employeesService.updateEmployee(formData)
      } else {
        response = await employeesService.saveEmployee(formData)
      }

      const result = (response as any)?.result
      const isSuccess =
        response?.success === true ||
        (result && result.isError === false) ||
        (response && typeof response === 'object' && ((response as any).id != null || (response as any)?.data != null))
      if (isSuccess) {
        await showSuccess(
          isEditing ? 'Updated' : 'Saved',
          isEditing ? 'Employee updated successfully.' : 'New record has been saved.'
        )
        navigate('/employees')
      } else {
        const msg =
          (result?.msg && String(result.msg).trim()) ||
          ((response as any)?.message ?? (response as any)?.Message ?? 'Failed to save employee.')
        await showError('Save failed', msg)
      }
    } catch (error: any) {
      const status = error?.response?.status
      const data = error?.response?.data
      if (import.meta.env.DEV) {
        console.error('Employee save error:', { status, data, error: error?.message })
      }
      let msg =
        (data && typeof data === 'object' && (data.message ?? data.Message)) ||
        (typeof data === 'string' ? data : null) ||
        error?.message ||
        'Failed to save employee.'
      if (Array.isArray(data?.errors) && data.errors.length) {
        msg = data.errors.join('\n')
      } else if (data?.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
        msg = Object.entries(data.errors)
          .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`)
          .join('\n')
      }
      const title = status ? `Save failed (${status})` : 'Save failed'
      await showError(title, String(msg))
    } finally {
      setLoading(false)
    }
  }

  const getEmployeeInitials = () => {
    const first = watch('EmpFirstNameEn') || watch('EmpFirstNameAr') || ''
    const family = watch('EmpFamilyNameEn') || watch('EmpFamilyNameAr') || ''
    if (first && family) return `${first.charAt(0)}${family.charAt(0)}`.toUpperCase()
    if (first) return first.slice(0, 2).toUpperCase()
    return 'NA'
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader
          title={isEditing ? 'Edit Employee' : 'Add New Employee'}
          description={isEditing ? 'Update employee information' : 'Add a new employee to your organization'}
        />
        <Card>
          <CardContent className="p-6">
            <p className="text-muted-foreground">Please select a branch first</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={isEditing ? 'Edit Employee' : 'Add New Employee'}
        description={isEditing ? 'Update employee information' : 'Add a new employee to your organization'}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-6">
          {/* Profile Image */}
      <Card>
        <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Photo
              </CardTitle>
        </CardHeader>
        <CardContent>
              <div className="flex items-center gap-6">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={imagePreview || undefined} alt="Profile" />
                  <AvatarFallback className="text-2xl">{getEmployeeInitials()}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Label htmlFor="profileImage">Upload Profile Image</Label>
                  <Input
                    id="profileImage"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="mt-2"
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Select an image file for the employee profile
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information - English */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information (English)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpFirstNameEn">
                    First Name (English) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpFirstNameEn"
                    {...register('EmpFirstNameEn')}
                    className={errors.EmpFirstNameEn ? 'border-destructive' : ''}
                  />
                  {errors.EmpFirstNameEn && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpFirstNameEn.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpFatherNameEn">Father Name (English)</Label>
                  <Input id="EmpFatherNameEn" {...register('EmpFatherNameEn')} />
                </div>
                <div>
                  <Label htmlFor="EmpGrandNameEn">Grand Name (English)</Label>
                  <Input id="EmpGrandNameEn" {...register('EmpGrandNameEn')} />
                </div>
                <div>
                  <Label htmlFor="EmpFamilyNameEn">Family Name (English)</Label>
                  <Input id="EmpFamilyNameEn" {...register('EmpFamilyNameEn')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Personal Information - Arabic */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information (Arabic)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpFirstNameAr">First Name (Arabic)</Label>
                  <Input id="EmpFirstNameAr" {...register('EmpFirstNameAr')} />
                </div>
                <div>
                  <Label htmlFor="EmpFatherNameAr">Father Name (Arabic)</Label>
                  <Input id="EmpFatherNameAr" {...register('EmpFatherNameAr')} />
                </div>
                <div>
                  <Label htmlFor="EmpGrandNameAr">Grand Name (Arabic)</Label>
                  <Input id="EmpGrandNameAr" {...register('EmpGrandNameAr')} />
                </div>
                <div>
                  <Label htmlFor="EmpFamilyNameAr">Family Name (Arabic)</Label>
                  <Input id="EmpFamilyNameAr" {...register('EmpFamilyNameAr')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact & Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact & Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpEmail">
                    Email <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpEmail"
                    type="email"
                    {...register('EmpEmail')}
                    className={errors.EmpEmail ? 'border-destructive' : ''}
                  />
                  {errors.EmpEmail && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpEmail.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpMobileNo">Mobile Number</Label>
                  <Input
                    id="EmpMobileNo"
                    {...register('EmpMobileNo')}
                    placeholder="+966XXXXXXXXX or 5XXXXXXXX"
                    className={errors.EmpMobileNo ? 'border-destructive' : ''}
                  />
                  {errors.EmpMobileNo && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpMobileNo.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpDob">
                    Date of Birth <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="EmpDob"
                    type="date"
                    {...register('EmpDob')}
                    className={errors.EmpDob ? 'border-destructive' : ''}
                  />
                  {errors.EmpDob && (
                    <p className="text-sm text-destructive mt-1">{errors.EmpDob.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="EmpAge">Age</Label>
                  <Input id="EmpAge" {...register('EmpAge')} />
                </div>
                <div>
                  <SearchableSelect
                    label={
                      <>
                        Gender <span className="text-destructive">*</span>
                      </>
                    }
                    options={(genders || []).map((gender) => ({
                      value: String(gender.id),
                      label: language === 'ar' ? (gender.lookupNameAr || gender.lookupNameEn || '') : (gender.lookupNameEn || gender.lookupNameAr || ''),
                    }))}
                    value={watch('EmpGenderId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpGenderId', Number(value))}
                    placeholder="Select gender"
                    searchPlaceholder="Search gender..."
                    emptyMessage="No gender found"
                    error={!!errors.EmpGenderId}
                    errorMessage={errors.EmpGenderId?.message}
                  />
                </div>
                <div>
                  <Label htmlFor="EmpBirthPlace">Birth Place</Label>
                  <Input id="EmpBirthPlace" {...register('EmpBirthPlace')} />
                </div>
                <div>
                  <SearchableSelect
                    label="Nationality"
                    options={(countries || []).map((country) => ({
                      value: String(country.id),
                      label: language === 'ar' ? (country.lookupNameAr || country.lookupNameEn || '') : (country.lookupNameEn || country.lookupNameAr || ''),
                    }))}
                    value={watch('EmpNationalityId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpNationalityId', Number(value))}
                    placeholder="Select nationality"
                    searchPlaceholder="Search nationality..."
                    emptyMessage="No nationality found"
                  />
                </div>
                <div>
                  <SearchableSelect
                    label="Marital Status"
                    options={(maritalStatuses || []).map((status) => ({
                      value: String(status.id),
                      label: language === 'ar' ? (status.lookupNameAr || status.lookupNameEn || '') : (status.lookupNameEn || status.lookupNameAr || ''),
                    }))}
                    value={watch('MaritalStatus')?.toString() || ''}
                    onValueChange={(value) => setValue('MaritalStatus', Number(value))}
                    placeholder="Select marital status"
                    searchPlaceholder="Search marital status..."
                    emptyMessage="No marital status found"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Employment Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Employment Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Row 1: Employee Code (edit only, read-only), Department, Posted Location */}
              <div className={`grid grid-cols-1 gap-4 ${isEditing ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                {isEditing && (
                  <div className="space-y-2">
                    <Label htmlFor="EmpCode">Employee Code</Label>
                    <Input id="EmpCode" {...register('EmpCode')} readOnly className="bg-muted" />
                  </div>
                )}
                <div className="space-y-2">
                  <SearchableSelect
                    label={<>Department</>}
                    options={(departments || []).map((dept) => ({
                      value: String(dept.id),
                      label: language === 'ar' ? (dept.lookupNameAr || dept.lookupNameEn || '') : (dept.lookupNameEn || dept.lookupNameAr || ''),
                    }))}
                    value={watch('EmpDepartmetId')?.toString() || ''}
                    onValueChange={(value) => setValue('EmpDepartmetId', Number(value))}
                    placeholder="Select department"
                    searchPlaceholder="Search department..."
                    emptyMessage="No department found"
                    error={!!errors.EmpDepartmetId}
                    errorMessage={errors.EmpDepartmetId?.message}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="EmpPostedLocation">Posted Location</Label>
                  <Input id="EmpPostedLocation" {...register('EmpPostedLocation')} />
                </div>
              </div>
              {/* Row 2: Notice Period, Contact Person Number, Shift, Is Active */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <SearchableSelect
                    label="Notice Period"
                    options={(noticePeriods || []).map((np) => ({
                      value: String(np.id),
                      label: language === 'ar' ? (np.lookupNameAr || np.lookupNameEn || '') : (np.lookupNameEn || np.lookupNameAr || ''),
                    }))}
                    value={watch('NoticePeriod')?.toString() || ''}
                    onValueChange={(value) => setValue('NoticePeriod', Number(value))}
                    placeholder="Select notice period"
                    searchPlaceholder="Search notice period..."
                    emptyMessage="No notice period found"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ContactPersonNumber">Contact Person Number</Label>
                  <Input id="ContactPersonNumber" {...register('ContactPersonNumber')} />
                </div>
                <div className="space-y-2">
                  <SearchableSelect
                    label="Shift"
                    options={(shifts || []).map((shift) => ({
                      value: String(shift.id ?? ''),
                      label: shift.name ?? shift.shiftType ?? shift.shiftDescription ?? shift.description ?? `Shift ${shift.id ?? ''}`,
                    })).filter((o) => o.value)}
                    value={watch('ShiftId')?.toString() || ''}
                    onValueChange={(value) => setValue('ShiftId', value ? Number(value) : 0)}
                    placeholder="Select shift"
                    searchPlaceholder="Search shift..."
                    emptyMessage="No shift found"
                  />
                </div>
                <div className="space-y-2">
                  <SearchableSelect
                    label="Is Active"
                    options={[
                      { value: '0', label: 'Not Active' },
                      { value: '1', label: 'Active' },
                    ]}
                    value={watch('IsActive')?.toString() ?? '1'}
                    onValueChange={(value) => setValue('IsActive', value === '1' ? 1 : 0)}
                    placeholder="Active"
                    searchPlaceholder="Search..."
                    emptyMessage="No option"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documents (Iqama, Passport, Profession, Other Nationality – API fields) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="IqamaNo">Iqama Number</Label>
                  <Input id="IqamaNo" {...register('IqamaNo')} />
                </div>
                <div>
                  <Label htmlFor="IqamaExpiryDate">Iqama Expiry Date</Label>
                  <Input id="IqamaExpiryDate" type="date" {...register('IqamaExpiryDate')} />
                </div>
                <div>
                  <Label htmlFor="PassportNo">Passport Number</Label>
                  <Input id="PassportNo" {...register('PassportNo')} />
                </div>
                <div>
                  <Label htmlFor="PassportExpiryDate">Passport Expiry Date</Label>
                  <Input id="PassportExpiryDate" type="date" {...register('PassportExpiryDate')} />
                </div>
                <div>
                  <SearchableSelect
                    label="Profession in Iqama"
                    options={(professions || []).map((prof) => ({
                      value: String(prof.id),
                      label: language === 'ar' ? (prof.lookupNameAr || prof.lookupNameEn || '') : (prof.lookupNameEn || prof.lookupNameAr || ''),
                    }))}
                    value={watch('ProfessionIqama')?.toString() || ''}
                    onValueChange={(value) => setValue('ProfessionIqama', Number(value))}
                    placeholder="Select profession"
                    searchPlaceholder="Search profession..."
                    emptyMessage="No profession found"
                  />
                </div>
                <div>
                  <SearchableSelect
                    label="Other Nationality"
                    options={(countries || []).map((country) => ({
                      value: String(country.id),
                      label: language === 'ar' ? (country.lookupNameAr || country.lookupNameEn || '') : (country.lookupNameEn || country.lookupNameAr || ''),
                    }))}
                    value={watch('OtherNationalityId')?.toString() || ''}
                    onValueChange={(value) => setValue('OtherNationalityId', Number(value))}
                    placeholder="Select nationality"
                    searchPlaceholder="Search nationality..."
                    emptyMessage="No nationality found"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reference Information (API: EmpReferenceId, EmpReferenceContact) */}
          <Card>
            <CardHeader>
              <CardTitle>Reference Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="EmpReferenceId">Reference ID</Label>
                  <Input id="EmpReferenceId" {...register('EmpReferenceId')} />
                </div>
                <div>
                  <Label htmlFor="EmpReferenceContact">Reference Contact</Label>
                  <Input id="EmpReferenceContact" {...register('EmpReferenceContact')} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate('/employees')} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} className="w-full sm:w-auto">
              {loading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update Employee' : 'Save Employee'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
